import React, { useState, useEffect } from 'react';
import { topicApi } from '../services/api.ts';

interface SeriesInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  onEditClick: () => void;
}

const SeriesInfoModal: React.FC<SeriesInfoModalProps> = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  onEditClick
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCustomContent, setIsCustomContent] = useState(false);

  useEffect(() => {
    if (isOpen && teamId) {
      loadSeriesInfo();
    }
  }, [isOpen, teamId]);

  const loadSeriesInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await topicApi.getSeriesInfo(teamId);
      setContent(response.content);
      setIsCustomContent(response.isCustomContent);
    } catch (err: any) {
      setError('Failed to load series information');
      console.error('Error loading series info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '800px',
          maxHeight: '80vh',
          width: '90%',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '20px',
          borderBottom: '1px solid #e0e0e0',
          paddingBottom: '16px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', color: '#333', fontSize: '1.5rem' }}>
              Series Information
            </h2>
            <h3 style={{ margin: 0, color: '#2563eb', fontSize: '1.25rem', fontWeight: '600' }}>
              {teamName}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Edit Information Section - Conditional display */}
        {!loading && !error && (
          <>
            {!isCustomContent ? (
              // Show grey box with message only for default template
              <div style={{ 
                backgroundColor: '#f9fafb',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Default template - Customize this content for your specific series!
                  </div>
                  
                  <button
                    onClick={onEditClick}
                    style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Edit Information
                  </button>
                </div>
              </div>
            ) : (
              // Show only Edit button for custom content
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                marginBottom: '20px'
              }}>
                <button
                  onClick={onEditClick}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Edit Information
                </button>
              </div>
            )}
          </>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading series information...</div>
          </div>
        )}

        {error && (
          <div style={{ 
            color: '#dc2626', 
            backgroundColor: '#fef2f2', 
            padding: '12px', 
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div 
              style={{ 
                marginBottom: '24px',
                lineHeight: '1.6'
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              paddingTop: '16px',
              borderTop: '1px solid #e0e0e0'
            }}>
              <button
                onClick={onClose}
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SeriesInfoModal;
